/**
 * Timeline Tab Component for Event Details Dialog
 */
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Calendar, Plus, Edit, Trash2, Clock, Printer } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function TimelineTab({ 
  booking, 
  timeline, 
  onTimelineUpdate,
  formatTime 
}) {
  const [showDialog, setShowDialog] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventData, setEventData] = useState({ time: '', title: '', description: '' });

  const sortedEvents = [...(timeline?.events || [])].sort((a, b) => {
    const timeA = a.time.replace(':', '');
    const timeB = b.time.replace(':', '');
    return timeA.localeCompare(timeB);
  });

  const openDialog = (event = null) => {
    if (event) {
      setEditingEvent(event);
      setEventData({ time: event.time, title: event.title, description: event.description || '' });
    } else {
      setEditingEvent(null);
      setEventData({ time: '', title: '', description: '' });
    }
    setShowDialog(true);
  };

  const handleSave = async () => {
    if (!eventData.time.trim() || !eventData.title.trim()) {
      toast.error('Please fill in time and title');
      return;
    }

    try {
      let events;
      if (editingEvent) {
        events = timeline.events.map(e => 
          e.id === editingEvent.id ? { ...e, ...eventData } : e
        );
      } else {
        events = [...(timeline?.events || []), {
          id: `evt_${Date.now()}`,
          ...eventData,
          created_at: new Date().toISOString()
        }];
      }

      if (timeline?.id) {
        await axios.put(`${API}/timelines/${timeline.id}`, { events }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      } else {
        await axios.post(`${API}/timelines`, {
          booking_id: booking.id,
          events
        }, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
      }

      onTimelineUpdate();
      setShowDialog(false);
      toast.success(editingEvent ? 'Event updated' : 'Event added');
    } catch (error) {
      toast.error('Failed to save event');
    }
  };

  const handleDelete = async (eventId) => {
    if (!window.confirm('Delete this timeline event?')) return;
    
    try {
      const events = timeline.events.filter(e => e.id !== eventId);
      await axios.put(`${API}/timelines/${timeline.id}`, { events }, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      onTimelineUpdate();
      toast.success('Event deleted');
    } catch (error) {
      toast.error('Failed to delete event');
    }
  };

  const handlePrint = () => {
    const printContent = `
      <html>
        <head>
          <title>Timeline - ${booking.client_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { font-size: 24px; margin-bottom: 5px; }
            .subtitle { color: #666; margin-bottom: 20px; }
            .event { display: flex; margin-bottom: 15px; padding-bottom: 15px; border-bottom: 1px solid #eee; }
            .time { font-weight: bold; width: 80px; color: #06b6d4; }
            .details { flex: 1; }
            .title { font-weight: 600; }
            .desc { color: #666; font-size: 14px; margin-top: 4px; }
          </style>
        </head>
        <body>
          <h1>${booking.client_name}'s ${booking.event_type}</h1>
          <div class="subtitle">${booking.event_date} - Timeline</div>
          ${sortedEvents.map(e => `
            <div class="event">
              <div class="time">${formatTime ? formatTime(e.time) : e.time}</div>
              <div class="details">
                <div class="title">${e.title}</div>
                ${e.description ? `<div class="desc">${e.description}</div>` : ''}
              </div>
            </div>
          `).join('')}
        </body>
      </html>
    `;
    const win = window.open('', '_blank');
    win.document.write(printContent);
    win.document.close();
    win.onload = () => win.print();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-semibold text-lg flex items-center gap-2">
          <Calendar className="w-5 h-5 text-neon-purple" />
          Event Timeline
        </h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={handlePrint} className="border-border">
            <Printer className="w-4 h-4 mr-1" />
            Print
          </Button>
          <Button size="sm" onClick={() => openDialog()} className="bg-neon-blue hover:bg-neon-blue/90">
            <Plus className="w-4 h-4 mr-1" />
            Add Event
          </Button>
        </div>
      </div>

      {sortedEvents.length > 0 ? (
        <div className="space-y-2">
          {sortedEvents.map((event) => (
            <div
              key={event.id}
              className="flex items-start gap-4 p-4 rounded-xl bg-muted/30 border border-border group"
            >
              <div className="flex items-center gap-2 min-w-[100px]">
                <Clock className="w-4 h-4 text-neon-blue" />
                <span className="font-mono text-neon-blue font-medium">
                  {formatTime ? formatTime(event.time) : event.time}
                </span>
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">{event.title}</p>
                {event.description && (
                  <p className="text-sm text-muted-foreground mt-1">{event.description}</p>
                )}
              </div>
              <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                <Button size="icon" variant="ghost" onClick={() => openDialog(event)} className="h-8 w-8">
                  <Edit className="w-4 h-4" />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => handleDelete(event.id)} className="h-8 w-8 text-red-400 hover:text-red-300">
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-muted-foreground">
          <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
          <p>No timeline events yet</p>
          <p className="text-sm">Add events to build the schedule</p>
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="bg-card border border-border">
          <DialogHeader>
            <DialogTitle>{editingEvent ? 'Edit Event' : 'Add Timeline Event'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium text-muted-foreground">Time</label>
              <Input
                type="time"
                value={eventData.time}
                onChange={(e) => setEventData({ ...eventData, time: e.target.value })}
                className="bg-muted border-border"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Title</label>
              <Input
                value={eventData.title}
                onChange={(e) => setEventData({ ...eventData, title: e.target.value })}
                placeholder="e.g., Ceremony Begins"
                className="bg-muted border-border"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-muted-foreground">Description (optional)</label>
              <Textarea
                value={eventData.description}
                onChange={(e) => setEventData({ ...eventData, description: e.target.value })}
                placeholder="Additional details..."
                className="bg-muted border-border"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
            <Button onClick={handleSave} className="bg-neon-blue hover:bg-neon-blue/90">
              {editingEvent ? 'Update' : 'Add Event'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
